import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { toClubInputValue, fromClubInputValue, formatClubTime } from '../../utils/clubTime';
import { deriveLifecycle, describeLifecycle, type CompetitionFacts, type Phase } from '@shared/competitionRules';
import ConfirmActionDialog from './ConfirmActionDialog';
import PublishChecklistDialog from './PublishChecklistDialog';
import UnpublishDialog from './UnpublishDialog';

const client = generateClient({ authMode: 'userPool' });

export interface CompetitionStatePanelEvent extends CompetitionFacts {
  id: string;
  title?: string | null;
  mysteryFishSpecies?: string | null;
  mysteryFishSize?: string | number | null;
  seniorChampionName?: string | null;
  juniorChampionName?: string | null;
}

type ActionType =
  | 'ADJUDICATE' | 'UNDO_ADJUDICATION' | 'PUBLISH' | 'UNPUBLISH'
  | 'CANCEL' | 'REINSTATE' | 'START_NOW' | 'EXTEND_SIGN_ON' | 'EXTEND_END'
  | 'END_NOW' | 'RESCHEDULE';

const PHASE_LABEL: Record<Phase, string> = {
  CANCELLED: 'Cancelled',
  PUBLISHED: 'Published',
  ADJUDICATED: 'Adjudicated',
  TIMING_INCOMPLETE: 'Timing incomplete',
  ENDED: 'Ended',
  FISHING: 'Fishing',
  SIGN_ON: 'Sign-on open',
  SCHEDULED: 'Scheduled',
};

const PHASE_COLOUR: Record<Phase, string> = {
  CANCELLED: 'bg-red-100 text-red-800',
  PUBLISHED: 'bg-purple-100 text-purple-800',
  ADJUDICATED: 'bg-blue-100 text-blue-800',
  TIMING_INCOMPLETE: 'bg-red-100 text-red-800',
  ENDED: 'bg-yellow-100 text-yellow-800',
  FISHING: 'bg-green-100 text-green-800',
  SIGN_ON: 'bg-teal-100 text-teal-800',
  SCHEDULED: 'bg-gray-100 text-gray-800',
};

/** Plain-English consequence shown before a quick action or decision runs. */
const ACTION_CONSEQUENCE: Record<string, string> = {
  START_NOW: 'Fishing starts now, even if that is before the scheduled start time.',
  END_NOW: 'Catches stop being accepted immediately for everyone.',
  ADJUDICATE: 'Marking adjudicated ends the catch review. You can publish the results after this.',
  UNDO_ADJUDICATION: 'Undoing adjudication reopens the catch review. Approve and disqualify stay available either way.',
  REINSTATE: 'Reinstating removes the cancellation. The competition returns to its normal lifecycle.',
};

interface CompetitionStatePanelProps {
  event: CompetitionStatePanelEvent;
  /** Submissions and champion standings, for the publish checklist — same shape AdjudicationDashboard already loads. */
  submissions?: any[];
  championStandings?: { seniorStandings: any[]; juniorStandings: any[] };
  /** Called after any action succeeds, so the caller can reload the event. */
  onRefresh: () => void | Promise<void>;
}

/**
 * The shared competition lifecycle panel (#134 D1). Shows the derived phase
 * chip and the two status lines, quick time actions, decisions (adjudicate,
 * undo, cancel, reinstate), and the publish/unpublish dialogs. Every action
 * goes through the single `adjudicateCatch` mutation's `actionType` argument
 * (the contract built by sub-agent C); after success the event is refreshed
 * and the server's plain-English message is shown in an aria-live region.
 */
const CompetitionStatePanel: React.FC<CompetitionStatePanelProps> = ({
  event, submissions, championStandings, onRefresh,
}) => {
  const [working, setWorking] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmAction, setConfirmAction] = useState<ActionType | null>(null);
  const [timeValue, setTimeValue] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);

  const now = new Date();
  const lifecycle = deriveLifecycle(event, now);
  const { signOn, catches } = describeLifecycle(event, now, formatClubTime);

  const runAction = async (actionType: ActionType, args: Record<string, unknown> = {}) => {
    setWorking(true);
    setStatusMessage(null);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType,
        eventId: event.id,
        ...args,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.error || 'The action was refused.');
      }
      setStatusMessage({ message: data.message || 'Done.', type: 'success' });
      await onRefresh();
    } catch (err: any) {
      setStatusMessage({ message: err.message || 'The action failed.', type: 'error' });
    } finally {
      setWorking(false);
    }
  };

  const openTimeDialog = (action: ActionType, initial: Date | null) => {
    setTimeValue(toClubInputValue(initial ? initial.toISOString() : null));
    setConfirmAction(action);
  };

  const closeDialog = () => {
    setConfirmAction(null);
    setTimeValue('');
    setCancelReason('');
  };

  const confirmTimeAction = async () => {
    const action = confirmAction;
    if (!action) return;
    const until = fromClubInputValue(timeValue);
    closeDialog();
    if (action === 'RESCHEDULE') {
      await runAction('RESCHEDULE', { newEventDate: until });
    } else if (action === 'EXTEND_SIGN_ON' || action === 'EXTEND_END') {
      await runAction(action, { until });
    }
  };

  const confirmSimpleAction = async () => {
    const action = confirmAction;
    closeDialog();
    if (!action) return;
    if (action === 'CANCEL') {
      await runAction('CANCEL', { reason: cancelReason });
    } else {
      await runAction(action);
    }
  };

  const handlePublished = async () => {
    setStatusMessage({ message: 'Results published.', type: 'success' });
    await onRefresh();
  };

  const handleUnpublished = async () => {
    setStatusMessage({ message: 'Results unpublished.', type: 'success' });
    await onRefresh();
  };

  const title = event.title || 'this competition';

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
      {statusMessage && (
        <div
          role="status"
          aria-live="polite"
          className={`mb-4 rounded-md border p-3 text-sm font-medium ${
            statusMessage.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {statusMessage.message}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${PHASE_COLOUR[lifecycle.phase]}`}>
          {PHASE_LABEL[lifecycle.phase]}
        </span>
      </div>

      <div className="space-y-1 mb-5 text-sm text-gray-700">
        <p>{signOn}</p>
        <p>{catches}</p>
      </div>

      {/* Quick time actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        {!lifecycle.started && !lifecycle.cancelled && !lifecycle.adjudicated && (
          <button
            type="button"
            onClick={() => setConfirmAction('START_NOW')}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md disabled:bg-gray-300"
          >
            Start now
          </button>
        )}
        {!lifecycle.cancelled && !lifecycle.adjudicated && (
          <button
            type="button"
            onClick={() => openTimeDialog('EXTEND_SIGN_ON', lifecycle.signOnAcceptsUntil)}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 disabled:bg-gray-50"
          >
            Extend sign-on
          </button>
        )}
        {!lifecycle.cancelled && !lifecycle.adjudicated && (
          <button
            type="button"
            onClick={() => openTimeDialog('EXTEND_END', lifecycle.catchDeadline)}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 disabled:bg-gray-50"
          >
            Extend the end
          </button>
        )}
        {lifecycle.started && !lifecycle.ended && !lifecycle.cancelled && !lifecycle.adjudicated && (
          <button
            type="button"
            onClick={() => setConfirmAction('END_NOW')}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md border border-red-300 disabled:bg-gray-50"
          >
            End now
          </button>
        )}
        <button
          type="button"
          onClick={() => openTimeDialog('RESCHEDULE', lifecycle.startsAt)}
          disabled={working || lifecycle.cancelled || lifecycle.adjudicated}
          className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 disabled:bg-gray-50"
        >
          Reschedule
        </button>
      </div>

      {/* Decisions */}
      <div className="flex flex-wrap gap-2 mb-4 pt-4 border-t border-gray-200">
        {lifecycle.ended && !lifecycle.adjudicated && !lifecycle.cancelled && (
          <button
            type="button"
            onClick={() => setConfirmAction('ADJUDICATE')}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md disabled:bg-gray-300"
          >
            Mark Adjudicated
          </button>
        )}
        {lifecycle.adjudicated && !lifecycle.published && (
          <button
            type="button"
            onClick={() => setConfirmAction('UNDO_ADJUDICATION')}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 disabled:bg-gray-50"
          >
            Undo adjudication
          </button>
        )}
        {lifecycle.adjudicated && !lifecycle.published && (
          <button
            type="button"
            onClick={() => setPublishDialogOpen(true)}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md disabled:bg-gray-300"
          >
            Publish Results
          </button>
        )}
        {lifecycle.published && (
          <button
            type="button"
            onClick={() => setUnpublishDialogOpen(true)}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md border border-red-300 disabled:bg-gray-50"
          >
            Unpublish results
          </button>
        )}
        {!lifecycle.cancelled && (
          <button
            type="button"
            onClick={() => setConfirmAction('CANCEL')}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md border border-red-300 disabled:bg-gray-50"
          >
            Cancel
          </button>
        )}
        {lifecycle.cancelled && (
          <button
            type="button"
            onClick={() => setConfirmAction('REINSTATE')}
            disabled={working}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 disabled:bg-gray-50"
          >
            Reinstate
          </button>
        )}
      </div>

      {/* Simple confirmations (Start now, End now, Adjudicate, Undo, Reinstate) */}
      {confirmAction && ['START_NOW', 'END_NOW', 'ADJUDICATE', 'UNDO_ADJUDICATION', 'REINSTATE'].includes(confirmAction) && (
        <ConfirmActionDialog
          title={`${confirmAction === 'START_NOW' ? 'Start now' : confirmAction === 'END_NOW' ? 'End now' : confirmAction === 'ADJUDICATE' ? 'Mark Adjudicated' : confirmAction === 'UNDO_ADJUDICATION' ? 'Undo adjudication' : 'Reinstate'}?`}
          message={ACTION_CONSEQUENCE[confirmAction] || 'This changes the competition for everyone.'}
          confirmLabel={confirmAction === 'START_NOW' ? 'Start now' : confirmAction === 'END_NOW' ? 'End now' : confirmAction === 'ADJUDICATE' ? 'Mark Adjudicated' : confirmAction === 'UNDO_ADJUDICATION' ? 'Undo adjudication' : 'Reinstate'}
          destructive={confirmAction === 'END_NOW'}
          onCancel={closeDialog}
          onConfirm={confirmSimpleAction}
        />
      )}

      {/* Cancel (with a reason) */}
      {confirmAction === 'CANCEL' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" aria-labelledby="cancel-competition-title" className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 id="cancel-competition-title" className="text-lg font-bold text-gray-900">Cancel {title}?</h2>
            <p className="mt-2 text-sm text-gray-700">
              Cancelling stops sign-on, check-in and catch submission for everyone. The competition is not deleted.
            </p>
            <label htmlFor="cancel-reason" className="mt-4 block text-sm font-medium text-gray-700">
              Reason
            </label>
            <input
              id="cancel-reason"
              type="text"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Severe weather warning"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
            />
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={closeDialog} className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSimpleAction}
                disabled={!cancelReason.trim()}
                className="min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300"
              >
                Cancel competition
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend sign-on / Extend the end / Reschedule — club-time picker */}
      {confirmAction && ['EXTEND_SIGN_ON', 'EXTEND_END', 'RESCHEDULE'].includes(confirmAction) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" aria-labelledby="time-action-title" className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 id="time-action-title" className="text-lg font-bold text-gray-900">
              {confirmAction === 'EXTEND_SIGN_ON' ? 'Extend sign-on' : confirmAction === 'EXTEND_END' ? 'Extend the end' : 'Reschedule'}
            </h2>
            <p className="mt-2 text-sm text-gray-700">
              {confirmAction === 'EXTEND_SIGN_ON' && 'Sign-on will accept new check-ins until the time below.'}
              {confirmAction === 'EXTEND_END' && 'Catches will be accepted until the time below.'}
              {confirmAction === 'RESCHEDULE' && 'Every competition time moves to keep the same offset from the new fishing start.'}
            </p>
            <label htmlFor="time-action-value" className="mt-4 block text-sm font-medium text-gray-700">
              {confirmAction === 'RESCHEDULE' ? 'New fishing start' : 'Until'}
            </label>
            <input
              id="time-action-value"
              type="datetime-local"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
            />
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={closeDialog} className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmTimeAction}
                disabled={!timeValue}
                className="min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:bg-gray-300"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {publishDialogOpen && (
        <PublishChecklistDialog
          event={event}
          submissions={submissions}
          championStandings={championStandings}
          onClose={() => setPublishDialogOpen(false)}
          onPublished={handlePublished}
        />
      )}

      {unpublishDialogOpen && (
        <UnpublishDialog
          event={event}
          onClose={() => setUnpublishDialogOpen(false)}
          onUnpublished={handleUnpublished}
        />
      )}
    </div>
  );
};

export default CompetitionStatePanel;
